import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import {
  SECTION_CARD_HEADER,
  SECTION_CARD_HEADER_MAIN,
  SECTION_CARD_ICON,
  SECTION_CARD_TITLE_COL,
  SECTION_CARD_TITLE,
} from '../ui/DetailSection';
import { AlertMessage } from '../ui/AlertMessage';
import { Spinner } from '../ui/Spinner';
import { useToast } from '../ui/Toast';
import { OrderTimeline } from './OrderTimeline';
import { PostProductionTrips } from './PostProductionTrips';
import { OrderActionBar } from './OrderActionBar';
import { OrderActivityTab } from './OrderActivityTab';
import {
  HoldModal,
  ConfirmTransitionModal,
  StartBeamModal,
  CompleteBeamModal,
  BeamHoldModal,
  VendorSendModal,
  VendorReceiveModal,
  DispatchModal,
  DeliverModal,
} from './OrderActionModals';
import {
  buildVendorTripsFromActivities,
  canonicalStatus,
  resolveDispatchDamagedPieces,
  statusMeta,
  statusLabel,
  TONE_BG,
} from './workflowConstants';
import {
  PartySection,
  KhataSection,
  TransportSection,
  BrokerSection,
  DesignSection,
  OrderInfoSection,
  StageVendorSection,
  BeamPlanSection,
} from './OrderFormSections';
import { endpoints } from '../../api/endpoints';
import {
  API_BASE_URL,
  buildEmptyOrderForm,
  buildOrderPayload,
  buildDraftOrderPayload,
  buildOperationalDraftPayload,
  buildDefaultBeamPlanFromFeeders,
  mapOrderToForm,
  resolveBeamsForPersist,
  validateOrderForm,
  validateOrderFormOperational,
  buildOperationalOrderPayload,
  validateOrderApproveBeamPlan,
  draftFormFingerprint,
  isEmptyDraftOrder,
  formatOrderPickDisplay,
  collectPdfExportableLedgerRows,
  isBeamPdfExportable,
} from './orderFormHelpers';
import { localCalendarDateString } from '../../utils/localDate';
import { AuthenticatedImage } from '../ui/AuthenticatedImage';
import { useModuleAccess } from '../../permissions/useModuleAccess';
import { useCommercialAccess } from '../../permissions/useCommercialAccess';
import { ModuleAccessBanner } from '../ui/ModuleAccessBanner';
import { pathForMenu } from '../../routes/menuPaths';
import { stashDesignReturnOrderId } from './orderReturnContext';
import {
  PartyQuickCreateModal,
  BrokerQuickCreateModal,
  TransportQuickCreateModal,
} from './QuickMasterCreateModals';

/**
 * Unified Order page — one surface for create, edit and view.
 *
 * Modes:
 *  - create  → orderId is null/undefined. All sections editable. Save = POST.
 *  - edit    → order is still in DRAFT. Fields editable. Save = PUT.
 *  - view    → APPROVED+. Read-only sections plus timeline, beam summary in the plan grid,
 *              Activity log, and the contextual action bar.
 */
export function OrderDetail({ orderId: initialOrderId, initialSelectDesignId, onBack, onDraftCreated }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { canCreate, canEdit: canEditPermission, isReadOnly } = useModuleAccess('ORDERS');
  const canSeeCommercial = useCommercialAccess();

  const [orderId, setOrderId] = useState(initialOrderId || null);
  /** True while route is /orders/new — not tied to whether a server draft exists yet. */
  const isCreateFlow = initialOrderId == null;

  const orderBodyScrollRef = useRef(null);

  const scrollOrderToTop = useCallback(() => {
    orderBodyScrollRef.current?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    const main = document.getElementById('dashboard-main-scroll');
    main?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useLayoutEffect(() => {
    scrollOrderToTop();
    window.scrollTo(0, 0);
  }, [initialOrderId, scrollOrderToTop]);

  /** Create flow: second pass after children mount so nothing scrolls back down (e.g. focus). */
  useEffect(() => {
    if (initialOrderId) return undefined;
    const t = setTimeout(scrollOrderToTop, 120);
    return () => clearTimeout(t);
  }, [initialOrderId, scrollOrderToTop]);

  // server state
  const [order, setOrder] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingOrder, setLoadingOrder] = useState(Boolean(initialOrderId));
  const [loadingActivity, setLoadingActivity] = useState(Boolean(initialOrderId));
  const [error, setError] = useState(null);

  // lookups
  const [parties, setParties] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [vendorParties, setVendorParties] = useState({
    buttaCutting: [],
    millFinish: [],
    borderCutting: [],
    handwork: [],
  });
  const [khatas, setKhatas] = useState([]);
  const [transportParties, setTransportParties] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [khataMachinesForStart, setKhataMachinesForStart] = useState([]);

  // form
  const [form, setForm] = useState(buildEmptyOrderForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [draftCreating, setDraftCreating] = useState(false);
  const [partyModalOpen, setPartyModalOpen] = useState(false);
  const [brokerModalOpen, setBrokerModalOpen] = useState(false);
  const [transportModalOpen, setTransportModalOpen] = useState(false);
  const draftSavingRef = useRef(false);
  const persistedDraftFingerprintRef = useRef('');
  const [mode, setMode] = useState(initialOrderId ? 'view' : 'edit');
  const [pickerAnchor, setPickerAnchor] = useState(null);

  // interaction state
  const [busy, setBusy] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  // beam-level modals
  const [startBeam, setStartBeam] = useState(null);
  const [completeBeam, setCompleteBeam] = useState(null);
  const [beamHoldModal, setBeamHoldModal] = useState(null); // { beam, mode: 'HOLD' | 'RESUME' }

  // order-level vendor / transition modals
  const [vendorSendModal, setVendorSendModal] = useState(null); // { stage }
  const [vendorReceiveModal, setVendorReceiveModal] = useState(null); // { stage, totalPieces }
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [pdfSelectedRows, setPdfSelectedRows] = useState(() => new Set());
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const beamPlanCount = form.beamPlan?.beams?.length ?? 0;

  const exportableLedgerRows = useMemo(
    () => collectPdfExportableLedgerRows(order?.beams),
    [order?.beams]
  );

  const exportableLedgerIds = useMemo(
    () => exportableLedgerRows.map((row) => String(row.id)),
    [exportableLedgerRows]
  );

  useEffect(() => {
    if (!orderId || exportableLedgerIds.length === 0) {
      setPdfSelectedRows(new Set());
      return;
    }
    setPdfSelectedRows(new Set(exportableLedgerIds));
  }, [orderId, exportableLedgerIds.join('|')]);

  // ------------------------------------------------------------------ helpers
  const authFetch = useCallback(async (path, init) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init && init.headers),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const text = await res.text();
    const body = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = new Error(body?.error || body?.message || `Request failed (${res.status})`);
      err.payload = body;
      err.status = res.status;
      throw err;
    }
    return body;
  }, []);

  const togglePdfRow = useCallback((ledgerId) => {
    const id = String(ledgerId);
    setPdfSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllPdfRows = useCallback(
    (checked) => {
      if (checked) {
        setPdfSelectedRows(new Set(exportableLedgerIds));
      } else {
        setPdfSelectedRows(new Set());
      }
    },
    [exportableLedgerIds]
  );

  const downloadOrderPdf = useCallback(async () => {
    if (!orderId) return;
    if (exportableLedgerIds.length === 0) {
      toast.error('PDF is available only for started beams with a machine assigned');
      return;
    }
    if (pdfSelectedRows.size === 0) {
      toast.error('Select at least one started machine line for the PDF');
      return;
    }
    setPdfDownloading(true);
    try {
      const selected = [...pdfSelectedRows];
      const allSelected = selected.length >= exportableLedgerIds.length;
      const rowIds = allSelected ? undefined : selected;
      const path = endpoints.orders.pdf(orderId, rowIds);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `PDF download failed (${res.status})`);
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const fallback = String(order?.orderCode || orderId).replace(/[^\w.-]+/g, '_');
      a.download = match?.[1] || `${fallback}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(err?.message || 'PDF download failed');
    } finally {
      setPdfDownloading(false);
    }
  }, [orderId, exportableLedgerIds, pdfSelectedRows, order?.orderCode, toast]);

  // ------------------------------------------------------------------ loaders
  const reloadOrder = useCallback(async (id = orderId) => {
    if (!id) return;
    setLoadingOrder(true);
    try {
      const data = await authFetch(`/api/orders/${id}/detail`);
      setOrder(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingOrder(false);
    }
  }, [orderId, authFetch]);

  const reloadActivity = useCallback(async (id = orderId) => {
    if (!id) return;
    setLoadingActivity(true);
    try {
      const data = await authFetch(`/api/orders/${id}/activity`);
      setActivities(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingActivity(false);
    }
  }, [orderId, authFetch]);

  // Load lookups once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [partiesData, designsData, colorsData, khatasData, transportData, brokersData, butta, mill, border, hand] = await Promise.all([
          authFetch('/api/parties?category=PARTIES').catch(() => []),
          authFetch('/api/designs').catch(() => []),
          authFetch('/api/insights?type=COLOR').catch(() => []),
          authFetch('/api/khatas').catch(() => []),
          authFetch('/api/parties?category=TRANSPORT_PARTIES').catch(() => []),
          authFetch('/api/brokers').catch(() => []),
          authFetch('/api/parties?category=BUTTA_CUTTING_PARTIES').catch(() => []),
          authFetch('/api/parties?category=MILL_FINISH_PARTIES').catch(() => []),
          authFetch('/api/parties?category=BORDER_CUTTING_PARTIES').catch(() => []),
          authFetch('/api/parties?category=HANDWORK_PARTIES').catch(() => []),
        ]);
        if (cancelled) return;
        setParties(Array.isArray(partiesData) ? partiesData : []);
        setDesigns(Array.isArray(designsData) ? designsData : []);
        setKhatas(Array.isArray(khatasData) ? khatasData : []);
        setTransportParties(Array.isArray(transportData) ? transportData : []);
        setBrokers(Array.isArray(brokersData) ? brokersData : []);
        setColorOptions(Array.isArray(colorsData) ? colorsData : []);
        setVendorParties({
          buttaCutting: Array.isArray(butta) ? butta : [],
          millFinish: Array.isArray(mill) ? mill : [],
          borderCutting: Array.isArray(border) ? border : [],
          handwork: Array.isArray(hand) ? hand : [],
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  // Load order data when we have an id
  useEffect(() => {
    if (!orderId) return;
    reloadOrder(orderId);
    reloadActivity(orderId);
  }, [orderId, reloadOrder, reloadActivity]);

  useEffect(() => {
    setActivityOpen(false);
  }, [orderId]);

  useEffect(() => {
    if (!startBeam || !order?.khataId) {
      setKhataMachinesForStart([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await authFetch(`/api/khatas/${order.khataId}/machines`);
        if (!cancelled) setKhataMachinesForStart(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setKhataMachinesForStart([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startBeam, order?.khataId, authFetch]);

  const markDraftPersisted = useCallback((formState) => {
    persistedDraftFingerprintRef.current = draftFormFingerprint(formState, {
      commercial: canSeeCommercial,
      designs,
    });
  }, [canSeeCommercial, designs]);

  const isDraftDirty = useCallback(() => {
    return draftFormFingerprint(form, { commercial: canSeeCommercial, designs })
      !== persistedDraftFingerprintRef.current;
  }, [form, canSeeCommercial, designs]);

  const lastHydratedRef = useRef({ orderId: null, designCount: 0, updatedAt: null });
  const rememberHydration = useCallback((orderRow) => {
    lastHydratedRef.current = {
      orderId: orderRow?.id ?? null,
      designCount: designs.length,
      updatedAt: orderRow?.updatedAt ?? null,
    };
  }, [designs.length]);

  const saveDraftNow = useCallback(async ({ silent = true } = {}) => {
    if (draftSavingRef.current) return null;

    let activeOrderId = orderId;
    let activeOrder = order;

    if (!isDraftDirty() && activeOrderId) {
      if (!silent) toast?.success?.('Draft saved');
      return activeOrder;
    }

    if (!activeOrderId) {
      if (!canCreate || !canSeeCommercial) return null;
      setDraftCreating(true);
      try {
        const data = await authFetch('/api/orders/draft', {
          method: 'POST',
          body: JSON.stringify({ orderDate: localCalendarDateString() }),
        });
        activeOrderId = data.id;
        activeOrder = data;
        setOrderId(data.id);
        setOrder(data);
        markDraftPersisted(mapOrderToForm(data, designs));
      } catch (e) {
        if (!silent) toast?.error?.(e.message || 'Unable to start draft order');
        return null;
      } finally {
        setDraftCreating(false);
      }
    }

    if (!isDraftDirty()) {
      if (!silent) toast?.success?.('Draft saved');
      return activeOrder;
    }

    draftSavingRef.current = true;
    try {
      const prunedBeams = resolveBeamsForPersist(form.beamPlan?.beams || [], form.designId, designs);
      const formWithBeams = { ...form, beamPlan: { beams: prunedBeams } };
      const payload = canSeeCommercial
        ? buildDraftOrderPayload(formWithBeams, designs)
        : buildOperationalDraftPayload(formWithBeams, designs);
      const data = await authFetch(`/api/orders/${activeOrderId}/draft`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setOrder(data);
      const synced = mapOrderToForm(data, designs);
      setForm(synced);
      markDraftPersisted(synced);
      rememberHydration(data);
      if (!silent) toast?.success?.('Draft saved');
      return data;
    } catch (e) {
      if (!silent) toast?.error?.(e.message || 'Unable to save draft');
      return null;
    } finally {
      draftSavingRef.current = false;
    }
  }, [orderId, order, form, designs, authFetch, canCreate, canSeeCommercial, isDraftDirty, markDraftPersisted, rememberHydration, toast]);

  // Hydrate form once both the order and the design list are available
  useEffect(() => {
    if (!order) return;
    const mapped = mapOrderToForm(order, designs);
    const designHasFeeders = Boolean(
      mapped.designId
      && designs.find((d) => String(d.id) === String(mapped.designId))?.feeders?.length
    );
    const mappedFeedersMissing = (mapped.beamPlan?.beams?.[0]?.feeders?.length ?? 0) === 0;
    const last = lastHydratedRef.current;
    if (
      last.orderId === order.id
      && last.designCount === designs.length
      && last.updatedAt === order.updatedAt
      && !(designHasFeeders && mappedFeedersMissing)
    ) return;
    setForm(mapped);
    markDraftPersisted(mapped);
    rememberHydration(order);
  }, [order, designs, markDraftPersisted, rememberHydration]);

  /** Incomplete drafts opened from the list should land in edit mode, not view + Approve. */
  useEffect(() => {
    if (!order || isCreateFlow) return;
    if (canonicalStatus(order.workflowStatus) === 'DRAFT' && isEmptyDraftOrder(order)) {
      setMode('edit');
    }
  }, [order, isCreateFlow]);

  useEffect(() => {
    if (initialOrderId) return;
    markDraftPersisted(buildEmptyOrderForm());
  }, [initialOrderId, markDraftPersisted]);

  useEffect(() => {
    if (!initialSelectDesignId || !designs.length || mode !== 'edit') return;
    const id = String(initialSelectDesignId);
    if (String(form.designId) === id) return;
    setForm((w) => ({
      ...w,
      designId: id,
      beamPlan: null,
      beamPlanSourceDesignId: null,
    }));
  }, [initialSelectDesignId, designs, mode, form.designId]);

  useEffect(() => {
    if (!initialSelectDesignId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const designsData = await authFetch('/api/designs');
        if (!cancelled) setDesigns(Array.isArray(designsData) ? designsData : []);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialSelectDesignId, authFetch]);

  const ensureDraftOrderId = useCallback(async () => {
    if (orderId) return orderId;
    if (!canCreate || !canSeeCommercial) return null;
    setDraftCreating(true);
    try {
      const data = await authFetch('/api/orders/draft', {
        method: 'POST',
        body: JSON.stringify({ orderDate: localCalendarDateString() }),
      });
      setOrderId(data.id);
      setOrder(data);
      const mapped = mapOrderToForm(data, designs);
      setForm(mapped);
      markDraftPersisted(mapped);
      return data.id;
    } catch (e) {
      toast?.error?.(e.message || 'Unable to start draft order');
      return null;
    } finally {
      setDraftCreating(false);
    }
  }, [orderId, canCreate, canSeeCommercial, authFetch, designs, markDraftPersisted, toast]);

  const handleCreateDesign = useCallback(async () => {
    if (isDraftDirty()) {
      await saveDraftNow({ silent: true });
    }
    const id = orderId || (await ensureDraftOrderId());
    if (!id) return;
    stashDesignReturnOrderId(id);
    navigate(pathForMenu('Designs'), { state: { openCreateDesign: true } });
  }, [orderId, isDraftDirty, saveDraftNow, ensureDraftOrderId, navigate]);

  // When a new design is picked in edit mode, seed a default beam plan
  useEffect(() => {
    if (mode !== 'edit') return;
    const designId = String(form.designId || '').trim();
    if (!designId) return;
    if (form.beamPlanSourceDesignId === designId && form.beamPlan?.beams?.length) return;
    const design = designs.find((d) => String(d.id) === designId);
    if (!design) return;
    setForm((w) => ({
      ...w,
      beamPlanSourceDesignId: String(design.id),
      beamPlan: buildDefaultBeamPlanFromFeeders(design.feeders || []),
    }));
  }, [mode, form.designId, form.beamPlanSourceDesignId, designs]);

  // ------------------------------------------------------------------ derived
  const canonical = useMemo(
    () => (order ? canonicalStatus(order.workflowStatus) : 'DRAFT'),
    [order]
  );

  /** Order sheet PDF requires approval and at least one started machine line. */
  const canExportOrderPdf = useMemo(
    () => Boolean(orderId) && canonical !== 'DRAFT' && exportableLedgerIds.length > 0,
    [orderId, canonical, exportableLedgerIds.length]
  );

  const canEditDraft = useMemo(() => {
    if (isCreateFlow) return canCreate && canSeeCommercial;
    if (!canEditPermission) return false;
    if (!order) return false;
    return canonical === 'DRAFT';
  }, [isCreateFlow, canEditPermission, canCreate, canSeeCommercial, order, canonical]);

  useEffect(() => {
    if (isCreateFlow && (!canCreate || !canSeeCommercial)) {
      onBack?.();
    }
  }, [isCreateFlow, canCreate, canSeeCommercial, onBack]);

  useEffect(() => {
    if (isCreateFlow) return;
    if (mode === 'edit' && !canEditDraft) {
      setMode('view');
    }
  }, [mode, canEditDraft, isCreateFlow]);

  const statusInfo = useMemo(() => (order ? statusMeta(order.workflowStatus) : null), [order]);
  const vendorTrips = useMemo(() => buildVendorTripsFromActivities(activities), [activities]);
  const dispatchDamagedPieces = useMemo(
    () => (order ? resolveDispatchDamagedPieces(order, activities) : 0),
    [order, activities]
  );
  const showWorkflowSurface = order && canonical !== 'DRAFT';

  const allVendorParties = useMemo(
    () => [
      ...vendorParties.buttaCutting,
      ...vendorParties.millFinish,
      ...vendorParties.borderCutting,
      ...vendorParties.handwork,
    ],
    [vendorParties]
  );

  // ------------------------------------------------------------------ save
  const handleSave = useCallback(async () => {
    if (!canEditPermission) return;
    const useCommercialValidation = canSeeCommercial;
    const err = useCommercialValidation
      ? validateOrderForm(form)
      : validateOrderFormOperational(form);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      let activeOrderId = orderId;
      if (!activeOrderId) {
        const draft = await saveDraftNow({ silent: true });
        activeOrderId = draft?.id || orderId;
        if (!activeOrderId) {
          setFormError('Unable to create order — try Save draft first');
          return;
        }
      }
      const prunedBeams = resolveBeamsForPersist(form.beamPlan?.beams || [], form.designId, designs);
      const formWithBeams = { ...form, beamPlan: { beams: prunedBeams } };
      const payload = useCommercialValidation
        ? buildOrderPayload(formWithBeams, designs)
        : buildOperationalOrderPayload(formWithBeams, designs);
      const data = await authFetch(`/api/orders/${activeOrderId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast?.success?.('Order saved');
      await Promise.all([reloadOrder(activeOrderId), reloadActivity(activeOrderId)]);
      setOrder(data);
      markDraftPersisted(formWithBeams);
      setMode('view');
      if (isCreateFlow) {
        onDraftCreated?.(activeOrderId);
      }
    } catch (e) {
      setFormError(e.message || 'Unable to save order');
      toast?.error?.(e.message || 'Unable to save order');
    } finally {
      setSaving(false);
    }
  }, [form, orderId, authFetch, toast, reloadOrder, reloadActivity, canEditPermission, canSeeCommercial, markDraftPersisted, saveDraftNow, isCreateFlow, onDraftCreated, designs]);

  const abandonCreateFlow = useCallback(async () => {
    if (!orderId || !order) return;
    if (isDraftDirty()) {
      await saveDraftNow({ silent: true });
      return;
    }
    if (isEmptyDraftOrder(order)) {
      if (canEditPermission && canSeeCommercial) {
        try {
          await authFetch(`/api/orders/${orderId}`, { method: 'DELETE' });
        } catch {
          // ignore — list refresh will still run
        }
      }
    }
  }, [orderId, order, isDraftDirty, saveDraftNow, authFetch, canEditPermission, canSeeCommercial]);

  const handleBack = useCallback(async () => {
    if (isCreateFlow) {
      await abandonCreateFlow();
      onBack?.();
      return;
    }
    if (mode === 'edit' && canonical === 'DRAFT' && orderId && isDraftDirty()) {
      await saveDraftNow();
    }
    onBack?.();
  }, [isCreateFlow, abandonCreateFlow, onBack, mode, canonical, orderId, isDraftDirty, saveDraftNow]);

  const cancelEdit = useCallback(async () => {
    if (isCreateFlow) {
      await abandonCreateFlow();
      onBack?.();
      return;
    }
    setFormError('');
    if (order) {
      const mapped = mapOrderToForm(order, designs);
      setForm(mapped);
      markDraftPersisted(mapped);
    }
    setMode('view');
  }, [isCreateFlow, abandonCreateFlow, onBack, order, designs, markDraftPersisted]);

  // ------------------------------------------------------------------ actions
  const refresh = useCallback(async () => {
    if (!orderId) return;
    await Promise.all([reloadOrder(orderId), reloadActivity(orderId)]);
  }, [orderId, reloadOrder, reloadActivity]);

  const runAction = useCallback(async (path, body, successMessage) => {
    if (!orderId) return false;
    setBusy(true);
    setError(null);
    try {
      const data = await authFetch(`/api/orders/${orderId}${path}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      });
      toast?.success?.(successMessage || 'Done');
      // Workflow POSTs return the same detail shape as GET /detail — apply immediately so UI
      // (e.g. Edit visibility) updates without waiting on a second fetch that may lag.
      if (
        data &&
        data.id != null &&
        String(data.id) === String(orderId) &&
        (path === '/transition' || path === '/dispatch' || path === '/deliver')
      ) {
        setOrder(data);
        if (path === '/transition' && canonicalStatus(data.workflowStatus) !== 'DRAFT') {
          setMode('view');
        }
        await reloadActivity(orderId);
      } else {
        await refresh();
      }
      return true;
    } catch (e) {
      setError(e.message);
      toast?.error?.(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [orderId, authFetch, toast, refresh, reloadActivity]);

  // Sum up planned pieces going through a given vendor stage (used by Receive modal).
  const piecesAtVendor = useMemo(() => {
    if (!order) return 0;
    return order.totalPieces || 0;
  }, [order]);

  const designForOrder = useMemo(
    () => (!order ? null : designs.find((d) => String(d.id) === String(order.designId)) ?? null),
    [designs, order]
  );

  const handleAction = useCallback((code) => {
    if (!order) return;
    switch (code) {
      case 'HOLD':
        setHoldOpen(true); break;
      case 'RELEASE_HOLD':
        runAction('/release', {}, 'Hold released'); break;
      case 'APPROVE': {
        const design = designs.find((d) => String(d.id) === String(order.designId));
        const approveErr = validateOrderApproveBeamPlan(order, design);
        if (approveErr) {
          toast?.error?.(approveErr);
          break;
        }
        setConfirmModal({
          title: 'Approve order',
          description: 'Approving will lock party / design / beam plan and create per-beam tracking.',
          actionLabel: 'Approve',
          actionVariant: 'success',
          onSubmit: (payload) => runAction('/transition', { toStatus: 'APPROVED', ...payload }, 'Order approved'),
        });
        break;
      }
      case 'PLACE_ORDER':
        setConfirmModal({
          title: 'Place order',
          description: 'Move order to PLACE_ORDER so production work can begin.',
          actionLabel: 'Place order',
          actionVariant: 'primary',
          onSubmit: (payload) => runAction('/transition', { toStatus: 'PLACE_ORDER', ...payload }, 'Order placed'),
        });
        break;
      case 'START_BEAMS':
      case 'COMPLETE_BEAMS': {
        const el = typeof document !== 'undefined' ? document.getElementById('order-beam-plan') : null;
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        toast?.info?.('Use the Status column in the Beam & feeder plan for each beam.');
        break;
      }
      case 'CHOOSE_NEXT_STAGE':
        toast?.info?.('Select next stage from the Next step dropdown.');
        break;
      case 'SEND_BUTTA_CUTTING':
        setVendorSendModal({ stage: 'BUTTA_CUTTING' }); break;
      case 'SEND_MILL_FINISH':
        setVendorSendModal({ stage: 'MILL_FINISH' }); break;
      case 'SEND_BORDER_CUTTING':
        setVendorSendModal({ stage: 'BORDER_CUTTING' }); break;
      case 'SEND_HANDWORK':
        setVendorSendModal({ stage: 'HANDWORK' }); break;
      case 'RECEIVE_FROM_BUTTA_CUTTING':
        setVendorReceiveModal({ stage: 'BUTTA_CUTTING', totalPieces: piecesAtVendor }); break;
      case 'RECEIVE_FROM_MILL_FINISH':
        setVendorReceiveModal({ stage: 'MILL_FINISH', totalPieces: piecesAtVendor }); break;
      case 'RECEIVE_FROM_BORDER_CUTTING':
        setVendorReceiveModal({ stage: 'BORDER_CUTTING', totalPieces: piecesAtVendor }); break;
      case 'RECEIVE_FROM_HANDWORK':
        setVendorReceiveModal({ stage: 'HANDWORK', totalPieces: piecesAtVendor }); break;
      case 'READY_FOR_DISPATCH':
        setConfirmModal({
          title: 'Mark ready for dispatch',
          description: 'Order will be marked as READY_FOR_DISPATCH. Dispatch it next when the truck is loaded.',
          actionLabel: 'Mark ready',
          onSubmit: (payload) => runAction('/transition', { toStatus: 'READY_FOR_DISPATCH', ...payload }, 'Ready for dispatch'),
        });
        break;
      case 'PUT_IN_STOCK':
        setConfirmModal({
          title: 'Put order in stock',
          description:
            'Goods are ready but not being sent out yet. The order will appear on the Stock page so you can find it later and dispatch when the party is ready.',
          actionLabel: 'Put in stock',
          actionVariant: 'primary',
          onSubmit: (payload) => runAction(
            '/transition',
            { toStatus: 'IN_STOCK', note: payload?.note },
            'Order added to stock',
          ),
        });
        break;
      case 'DISPATCH':
        setDispatchOpen(true); break;
      case 'MARK_DELIVERED':
        setDeliverOpen(true); break;
      case 'CANCEL':
        setConfirmModal({
          title: 'Cancel order',
          description: 'Cancelling is permanent. Only possible before production starts.',
          actionLabel: 'Cancel order',
          actionVariant: 'danger',
          requireReason: true,
          onSubmit: (payload) => runAction('/transition', { toStatus: 'CANCELLED', ...payload }, 'Order cancelled'),
        });
        break;
      default:
        break;
    }
  }, [order, designs, runAction, toast, piecesAtVendor]);

  const handleBeamAction = useCallback((action, beam) => {
    if (!beam) return;
    switch (action) {
      case 'START':
        setStartBeam(beam); break;
      case 'COMPLETE':
        setCompleteBeam(beam); break;
      case 'HOLD':
        setBeamHoldModal({ beam, mode: 'HOLD' }); break;
      case 'RESUME':
        setBeamHoldModal({ beam, mode: 'RESUME' }); break;
      default:
        break;
    }
  }, []);

  // ------------------------------------------------------------------ render
  if (!isCreateFlow && loadingOrder && !order) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }
  if (!isCreateFlow && !order && !loadingOrder) {
    return (
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <AlertMessage type="error" message={error || 'Order not found'} />
      </div>
    );
  }

  const editing = mode === 'edit';
  const showTimeline = order && showWorkflowSurface;
  const showTabs = order && showWorkflowSurface;
  const beamsSummary = order?.beamsSummary ?? {};

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 overflow-visible border-b border-slate-200 bg-white shadow-sm">
        <div className="flex w-full items-center justify-between gap-3 py-3.5 pl-2 pr-2 sm:pl-3 sm:pr-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleBack}
            leftIcon={(
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            )}
          >
            Back to orders
          </Button>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {editing && canEditDraft ? (
              <>
                {isDraftDirty() ? (
                  <span className="hidden text-xs text-amber-700 sm:inline">Unsaved changes</span>
                ) : null}
                <Button variant="secondary" size="sm" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => saveDraftNow({ silent: false })}
                  disabled={saving || !isDraftDirty()}
                >
                  Save draft
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
                  Save order
                </Button>
              </>
            ) : (
              <>
                {canEditDraft && !isCreateFlow && (
                  <Button variant="secondary" size="sm" onClick={() => setMode('edit')}>
                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit
                  </Button>
                )}
                {order && canEditPermission && !isCreateFlow && !(canonical === 'DRAFT' && isEmptyDraftOrder(order)) && (
                  <OrderActionBar order={order} onAction={handleAction} canSeeCommercial={canSeeCommercial} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div
        ref={orderBodyScrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-auto"
      >
      <div className="space-y-6 pt-4 sm:pt-5">
      {!isCreateFlow && isReadOnly && <ModuleAccessBanner moduleKey="ORDERS" />}
      {error && <AlertMessage type="error" message={error} onClose={() => setError(null)} />}
      {/* ── Order details ── */}
      {order && (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm sm:p-5">
          <header className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-slate-200/80 pb-3">
            <h2 className="text-base font-extrabold tracking-tight text-slate-900 sm:text-lg">
              Order details
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                  TONE_BG[statusInfo?.tone] || TONE_BG.slate
                }`}
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-75" />
                {statusLabel(canonical)}
              </span>
              {order.onHold && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-200">
                  On hold
                </span>
              )}
              {editing && !isCreateFlow && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-bold text-primary-700 ring-1 ring-primary-200">
                  Editing
                </span>
              )}
            </div>
          </header>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <HeaderField label="Order ID" value={order.orderCode || order.id} />
            <HeaderField label="Design" value={order.designSampleNo} />
            {canSeeCommercial ? (
              <>
                <HeaderField label="Party" value={order.partyName} />
                {order.brokerName ? <HeaderField label="Broker" value={order.brokerName} /> : null}
                <HeaderField label="Transport" value={order.transportServiceName} />
              </>
            ) : null}
            <HeaderField label="Production unit" value={order.khataName} />
            <HeaderField label="Total pieces" value={order.totalPieces} />
            <HeaderField label="Order date" value={formatDate(order.orderDate)} />
            <HeaderField label="Pick" value={formatOrderPickDisplay(order, designs)} />
            {canSeeCommercial && order.price != null && order.price !== '' && (
              <HeaderField label="Price" value={formatOrderPrice(order.price)} />
            )}
            {order.stockedAt && <HeaderField label="Stocked" value={formatDateTime(order.stockedAt)} />}
            {order.dispatchDate && <HeaderField label="Dispatched" value={formatDate(order.dispatchDate)} />}
            {order.deliveryDate && <HeaderField label="Delivered" value={formatDate(order.deliveryDate)} />}
          </dl>
          {order.onHold && order.holdNote && (
            <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-200">
              <span className="font-semibold">On hold ({order.holdReason}):</span> {order.holdNote}
            </div>
          )}
          {formError && editing && (
            <div className="mt-4">
              <AlertMessage type="error" message={formError} onClose={() => setFormError('')} />
            </div>
          )}
        </section>
      )}
      {!order && formError && editing && (
        <AlertMessage type="error" message={formError} onClose={() => setFormError('')} />
      )}

      {/* Form — one guided flow: design first, then customer/logistics, then terms */}
      <div className="space-y-8">
        <OrderFormStepGroup
          step={1}
          title="Design & production plan"
        >
          <DesignSection
            readOnly={!editing}
            form={form}
            setForm={setForm}
            designs={designs}
            designSnapshot={order}
            autoFocusSearch={Boolean(initialOrderId)}
            onCreateDesign={editing && canSeeCommercial ? handleCreateDesign : undefined}
          />
          <BeamPlanSection
            readOnly={!editing}
            form={form}
            setForm={setForm}
            designs={designs}
            colorOptions={colorOptions}
            openPickerAnchor={pickerAnchor}
            setOpenPickerAnchor={setPickerAnchor}
            runtimeBeams={!editing && order?.beams?.length ? order.beams : undefined}
            onBeamAction={handleBeamAction}
            workflowStatus={order?.workflowStatus}
            orderOnHold={!!order?.onHold}
            pdfExport={
              canExportOrderPdf
                ? {
                    enabled: true,
                    selectedRows: pdfSelectedRows,
                    exportableRowIds: exportableLedgerIds,
                    isRowExportable: isBeamPdfExportable,
                    onToggleRow: togglePdfRow,
                    onToggleAll: toggleAllPdfRows,
                    onDownload: downloadOrderPdf,
                    downloading: pdfDownloading,
                  }
                : undefined
            }
          />
          {editing && form.designId ? (
            <DesignPreviewBanner form={form} designs={designs} />
          ) : null}
        </OrderFormStepGroup>

        <OrderFormStepGroup
          step={2}
          title={canSeeCommercial ? 'Party, production site & logistics' : 'Production site & logistics'}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-4">
            {canSeeCommercial ? (
              <>
                <div className="min-w-0">
                  <PartySection
                    readOnly={!editing}
                    form={form}
                    setForm={setForm}
                    parties={parties}
                    partySnapshot={order}
                    onAddParty={editing && canSeeCommercial ? () => setPartyModalOpen(true) : undefined}
                  />
                </div>
                <div className="min-w-0">
                  <BrokerSection
                    readOnly={!editing}
                    form={form}
                    setForm={setForm}
                    brokers={brokers}
                    brokerSnapshot={order}
                    onAddBroker={editing && canSeeCommercial ? () => setBrokerModalOpen(true) : undefined}
                  />
                </div>
                <div className="min-w-0">
                  <TransportSection
                    readOnly={!editing}
                    form={form}
                    setForm={setForm}
                    transportParties={transportParties}
                    transportSnapshot={order}
                    onAddTransport={editing && canSeeCommercial ? () => setTransportModalOpen(true) : undefined}
                  />
                </div>
              </>
            ) : null}
            <div className="min-w-0">
              <KhataSection
                readOnly={!editing}
                form={form}
                setForm={setForm}
                khatas={khatas}
                khataSnapshot={order}
              />
            </div>
          </div>
        </OrderFormStepGroup>

        <OrderFormStepGroup
          step={3}
          title="Order terms & vendors"
        >
          <OrderInfoSection readOnly={!editing} form={form} setForm={setForm} showCommercial={canSeeCommercial} />
          <StageVendorSection
            readOnly={!editing}
            form={form}
            setForm={setForm}
            vendorParties={vendorParties}
            workflowStatus={order?.workflowStatus}
          />
        </OrderFormStepGroup>
      </div>

      {/* Summary & workflow widgets — below beam & feeder plan */}
      {showTimeline && (
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
          <SummaryPill label="Planned" value={beamsSummary.totalPlannedPieces ?? 0} tone="slate" />
          <SummaryPill label="Beams" value={beamsSummary.totalBeams || 0} tone="slate" />
          <SummaryPill label="Running" value={beamsSummary.runningBeams || 0} tone="teal" />
          <SummaryPill label="Paused" value={beamsSummary.onHoldBeams || 0} tone="amber" />
          <SummaryPill label="Completed" value={beamsSummary.completedBeams ?? beamsSummary.doneBeams ?? 0} tone="emerald" />
          <SummaryPill label="Completed" value={beamsSummary.totalProducedPieces ?? 0} tone="teal" />
          <SummaryPill label="Scrapped" value={beamsSummary.totalScrappedPieces ?? 0} tone="rose" />
        </section>
      )}
      {showTimeline && (
        <>
          <OrderTimeline status={canonical} onHold={order.onHold} activities={activities} />
          <PostProductionTrips trips={vendorTrips} />
        </>
      )}
      {showTabs && (
        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setActivityOpen((v) => !v)}
            className={`${SECTION_CARD_HEADER} w-full cursor-pointer text-left transition-colors hover:from-slate-100/80`}
            aria-expanded={activityOpen}
          >
            <div className={SECTION_CARD_HEADER_MAIN}>
              <span className={SECTION_CARD_ICON}>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
              <div className={SECTION_CARD_TITLE_COL}>
                <h3 className={SECTION_CARD_TITLE}>Activity</h3>
              </div>
            </div>
            <svg
              className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${activityOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {activityOpen ? (
            <div className="p-3 sm:p-4">
              <OrderActivityTab activities={activities} loading={loadingActivity} canSeeCommercial={canSeeCommercial} />
            </div>
          ) : null}
        </section>
      )}
      </div>
      </div>

      {/* Workflow modals */}
      {order && (
        <>
          <HoldModal
            open={holdOpen}
            onClose={() => setHoldOpen(false)}
            busy={busy}
            onSubmit={async (payload) => {
              const ok = await runAction('/hold', payload, 'Order placed on hold');
              if (ok) setHoldOpen(false);
            }}
          />
          <ConfirmTransitionModal
            open={!!confirmModal}
            onClose={() => setConfirmModal(null)}
            busy={busy}
            title={confirmModal?.title || ''}
            description={confirmModal?.description || ''}
            actionLabel={confirmModal?.actionLabel || 'Confirm'}
            actionVariant={confirmModal?.actionVariant}
            requireReason={confirmModal?.requireReason}
            onSubmit={async (payload) => {
              if (!confirmModal) return;
              const ok = await confirmModal.onSubmit(payload);
              if (ok) setConfirmModal(null);
            }}
          />

          <StartBeamModal
            open={!!startBeam}
            onClose={() => setStartBeam(null)}
            beam={startBeam}
            busy={busy}
            machineOptions={khataMachinesForStart}
            khataLinked={order.khataId != null && String(order.khataId).trim() !== ''}
            singleBeamDesign={order?.designSingleBeam === true || designForOrder?.singleBeam === true}
            plannedPieces={startBeam?.plannedPieces ?? 0}
            onSubmit={async (payload) => {
              const ok = await runAction(`/beams/${startBeam.beamNo}/start`, payload, `Beam #${startBeam.beamNo} started`);
              if (ok) setStartBeam(null);
            }}
          />
          <CompleteBeamModal
            open={!!completeBeam}
            onClose={() => setCompleteBeam(null)}
            beam={completeBeam}
            busy={busy}
            onSubmit={async (payload) => {
              const lid = completeBeam?.id;
              const path = lid != null ? `/beam-rows/${lid}/complete` : `/beams/${completeBeam.beamNo}/complete`;
              const ok = await runAction(path, payload, `Machine line completed (beam #${completeBeam.beamNo})`);
              if (ok) setCompleteBeam(null);
            }}
          />
          <BeamHoldModal
            open={!!beamHoldModal}
            onClose={() => setBeamHoldModal(null)}
            beam={beamHoldModal?.beam}
            mode={beamHoldModal?.mode}
            busy={busy}
            onSubmit={async (payload) => {
              if (!beamHoldModal) return;
              const endpoint = beamHoldModal.mode === 'HOLD' ? 'hold' : 'resume';
              const b = beamHoldModal.beam;
              const lid = b?.id;
              const path = lid != null ? `/beam-rows/${lid}/${endpoint}` : `/beams/${b.beamNo}/${endpoint}`;
              const ok = await runAction(
                path,
                payload,
                beamHoldModal.mode === 'HOLD' ? 'Machine line paused' : 'Machine line resumed'
              );
              if (ok) setBeamHoldModal(null);
            }}
          />

          <VendorSendModal
            open={!!vendorSendModal}
            onClose={() => setVendorSendModal(null)}
            stage={vendorSendModal?.stage}
            vendorParties={allVendorParties}
            busy={busy}
            defaults={{ chalanNo: order?.chalanNo }}
            onSubmit={async (payload) => {
              const ok = await runAction('/vendor/send', payload, 'Sent to vendor');
              if (ok) setVendorSendModal(null);
            }}
          />
          <VendorReceiveModal
            open={!!vendorReceiveModal}
            onClose={() => setVendorReceiveModal(null)}
            stage={vendorReceiveModal?.stage}
            totalPieces={vendorReceiveModal?.totalPieces}
            busy={busy}
            onSubmit={async (payload) => {
              const ok = await runAction('/vendor/receive', payload, 'Received from vendor');
              if (ok) setVendorReceiveModal(null);
            }}
          />
          <DispatchModal
            open={dispatchOpen}
            onClose={() => setDispatchOpen(false)}
            busy={busy}
            defaults={{
              price: order.price,
              billNo: order.billNo,
              chalanNo: order.chalanNo,
              damagedPieces: dispatchDamagedPieces,
              damagedFromProduction: dispatchDamagedPieces,
              totalPieces: order.totalPieces,
            }}
            onSubmit={async (payload) => {
              const ok = await runAction('/dispatch', payload, 'Order dispatched');
              if (ok) setDispatchOpen(false);
              return ok;
            }}
          />
          <DeliverModal
            open={deliverOpen}
            onClose={() => setDeliverOpen(false)}
            busy={busy}
            orderId={orderId}
            defaults={{
              dispatchInvoiceImage: order.dispatchInvoiceImage,
              dispatchLrImage: order.dispatchLrImage,
            }}
            onSubmit={async (payload) => {
              const ok = await runAction('/deliver', payload, 'Order delivered');
              if (ok) setDeliverOpen(false);
              return ok;
            }}
          />
        </>
      )}

      <PartyQuickCreateModal
        open={partyModalOpen}
        onClose={() => setPartyModalOpen(false)}
        onCreated={(party) => {
          setParties((prev) => [party, ...prev]);
          setForm((w) => ({ ...w, partyId: String(party.id), deliveryDetailId: '' }));
        }}
      />
      <BrokerQuickCreateModal
        open={brokerModalOpen}
        onClose={() => setBrokerModalOpen(false)}
        onCreated={(broker) => {
          setBrokers((prev) => [broker, ...prev]);
          setForm((w) => ({ ...w, brokerId: String(broker.id), brokerName: broker.name || '' }));
        }}
      />
      <TransportQuickCreateModal
        open={transportModalOpen}
        onClose={() => setTransportModalOpen(false)}
        onCreated={(party) => {
          setTransportParties((prev) => [party, ...prev]);
          setForm((w) => ({
            ...w,
            transportPartyId: String(party.id),
            transportServiceName: party.name || '',
          }));
        }}
      />
    </div>
  );
}

/** Groups related order form blocks under a numbered step for clearer reading order. */
function OrderFormStepGroup({ step, title, children }) {
  return (
    <section
      className="rounded-2xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-sm sm:p-5"
      aria-labelledby={`order-step-${step}-title`}
    >
      <header className="mb-4 flex flex-wrap items-start gap-3 border-b border-slate-200/80 pb-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-black text-white shadow-sm ring-2 ring-primary-500/25"
          aria-hidden
        >
          {step}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={`order-step-${step}-title`} className="text-base font-extrabold tracking-tight text-slate-900 sm:text-lg">
            {title}
          </h2>
        </div>
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

// ------------------------- Small presentational helpers -------------------------
function formatOrderPrice(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v ?? '—');
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function HeaderField({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 truncate text-sm font-semibold text-slate-800">{value || '—'}</dd>
    </div>
  );
}

function SummaryPill({ label, value, tone }) {
  const toneCls = {
    slate: 'bg-slate-50 text-slate-700 ring-slate-200',
    teal: 'bg-teal-50 text-teal-700 ring-teal-200',
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    rose: 'bg-rose-50 text-rose-700 ring-rose-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  }[tone] || 'bg-slate-50 text-slate-700 ring-slate-200';
  return (
    <div className={`rounded-xl px-4 py-3 ring-1 ${toneCls}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-75">{label}</p>
      <p className="mt-1 text-2xl font-black tabular-nums">{value}</p>
    </div>
  );
}

function DesignPreviewBanner({ form, designs }) {
  const design = designs.find((d) => String(d.id) === String(form.designId));
  if (!design) return null;
  return (
    <section className="flex items-center gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
        {design.designImage ? (
          <AuthenticatedImage src={design.designImage} alt="" className="h-full w-full object-cover" />
        ) : null}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900">{design.sampleNo}</p>
        <p className="truncate text-xs text-slate-500">
          {[design.conceptName, design.categoryName].filter(Boolean).join(' · ') || 'Selected design'}
        </p>
      </div>
    </section>
  );
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return d;
  }
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return String(iso);
  }
}
